/* =============================================================
   EXPORT -- the one thing every Export button in the app runs

   There were six of these before this file: NewCustomDataTable
   built a CSV one way, the legacy Datatable another, ListAttendance
   two more, the trainer dashboard a fifth, StudentsReport a sixth,
   and the attendance analytics page had a button that logged to the
   console and told the reader it had exported. They disagreed on the
   BOM, on the escaping, on the file name and on whether anything was
   said afterwards.

   This is a plain module -- nothing here imports React, so a page can
   export without a dialog if it wants to. The dialog beside it is the
   house popup; this is the part that writes the file.

   COLUMN SHAPE. Every caller normalises to the same thing:

     { key: 'email', label: 'Email', value: (row, i) => row.email }

   `value` may return anything; it is stringified here. A column with
   no `value` reads row[key]. That one shape is what lets the dialog
   offer a column picker on a page it knows nothing else about.
   ============================================================= */

/** The day, as the file name wants it: 2026-09-12. */
const todayStamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * "Email Event Configs" -> "email-event-configs". Windows rejects
 * \ / : * ? " < > | in a file name and a browser will silently save a
 * name it does not like as "download", so nothing but a-z, 0-9 and a
 * hyphen survives.
 */
export const slugify = (value, fallback = 'export') => {
  const slug = String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
};

export const buildFilename = (base, extension) =>
  `${slugify(base)}-${todayStamp()}.${extension}`;

/** A column's text for one row, always a string. */
export const cellText = (column, row, index) => {
  if (!column) return '';
  const raw =
    typeof column.value === 'function'
      ? column.value(row, index)
      : row?.[column.key];
  if (raw == null) return '';
  if (raw instanceof Date) return raw.toLocaleDateString();
  if (Array.isArray(raw)) return raw.join('; ');
  if (typeof raw === 'object') return raw.name ?? JSON.stringify(raw);
  return String(raw);
};

/**
 * RFC 4180, and the two traps that come with it.
 *
 * A field is quoted when it holds a comma, a quote, a newline or a
 * carriage return, and every quote inside it is doubled. The old
 * copies quoted on comma and quote only, so a topic list with a line
 * break in it shifted every column after it onto a new row.
 *
 * A leading =, +, - or @ is prefixed with a tab. Excel and Sheets read
 * those as the start of a formula, so a cell holding "=cmd" is a
 * command the reader's spreadsheet offers to run. The tab makes it
 * text; it is invisible in the cell.
 */
const csvCell = (text) => {
  // A plain negative number is exempt. Guarding on the leading
  // character alone turned "-5" absences into text, and a column of
  // numbers that will not add up is a worse bug than the one the guard
  // is for.
  const risky = /^[=+\-@\t\r]/.test(text) && !/^-?\d+(\.\d+)?$/.test(text);
  const value = risky ? `\t${text}` : text;
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
};

/**
 * Rows of cells, before either writer sees them. Both formats are
 * built from this, so a CSV and an .xlsx of the same table can never
 * disagree about what was in it.
 *
 * `preamble` and `appendix` are whole lines -- a report title, when it
 * was generated, a list of topics under the table. They are not
 * columns and are not escaped as a row of them.
 */
const toMatrix = ({ columns, rows, preamble = [], appendix = [] }) => {
  const matrix = [];
  preamble.forEach((line) => matrix.push([line]));
  if (preamble.length) matrix.push([]);
  matrix.push(columns.map((c) => c.label));
  rows.forEach((row, i) => matrix.push(columns.map((c) => cellText(c, row, i))));
  if (appendix.length) {
    matrix.push([]);
    appendix.forEach((line) => matrix.push([line]));
  }
  return matrix;
};

export const buildCsv = (spec) =>
  toMatrix(spec)
    .map((line) => line.map((cell) => csvCell(String(cell ?? ''))).join(','))
    .join('\n');

/**
 * The anchor is appended before it is clicked: Firefox ignores a click
 * on an <a> that is not in the document. revokeObjectURL is deferred a
 * tick because Safari cancels a download whose URL is revoked in the
 * same task.
 */
const download = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/**
 * What the dialog offers, and what it says about each one. The note is
 * the reason to pick it, not a description of the extension.
 */
export const EXPORT_FORMATS = [
  {
    id: 'csv',
    label: 'CSV',
    extension: 'csv',
    note: 'Opens in anything. One sheet, plain text.',
  },
  {
    id: 'xlsx',
    label: 'Excel',
    extension: 'xlsx',
    note: 'Formatted workbook, columns sized to fit.',
  },
];

const writeCsv = (spec, filename) => {
  const csv = buildCsv(spec);
  // The BOM is what makes Excel read a name with an accent, a rupee
  // sign or an en dash as UTF-8 rather than as the system codepage.
  // Written as the escape, not as the character: the existing copies
  // carry a literal U+FEFF in the source, which is invisible in every
  // editor and is the first thing a "normalise whitespace" pass eats.
  download(
    new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }),
    filename,
  );
};

const writeXlsx = async (spec, filename, sheetName) => {
  // Loaded only when someone actually picks Excel. xlsx is ~400KB and
  // this component is on 27 pages; a static import would put all of it
  // in every one of their bundles for a button most readers never use.
  const XLSX = await import('xlsx');
  const matrix = toMatrix(spec);
  const sheet = XLSX.utils.aoa_to_sheet(matrix);

  // Column widths from the content. Without them every column is the
  // default 8 characters and a batch name arrives as ###.
  sheet['!cols'] = spec.columns.map((column, i) => {
    const longest = matrix.reduce(
      (max, line) => Math.max(max, String(line[i] ?? '').length),
      0,
    );
    return { wch: Math.min(Math.max(longest + 2, 10), 60) };
  });

  const book = XLSX.utils.book_new();
  // Excel rejects a sheet name over 31 characters or holding : \ / ? * [ ]
  const safeSheet = String(sheetName || 'Export')
    .replace(/[:\\/?*[\]]/g, ' ')
    .slice(0, 31)
    .trim();
  XLSX.utils.book_append_sheet(book, sheet, safeSheet || 'Export');
  XLSX.writeFile(book, filename);
};

/**
 * Writes the file and returns what was written, so the caller can say
 * so. Throws on failure -- the dialog reports it and stays open, which
 * is the one case where a toast alone would leave the reader with
 * nothing to try again.
 */
export const runExport = async ({
  format = 'csv',
  columns = [],
  rows = [],
  filenameBase = 'export',
  sheetName,
  preamble,
  appendix,
}) => {
  const chosen =
    EXPORT_FORMATS.find((f) => f.id === format) || EXPORT_FORMATS[0];
  const spec = { columns, rows, preamble, appendix };
  const filename = buildFilename(filenameBase, chosen.extension);

  if (chosen.id === 'xlsx') await writeXlsx(spec, filename, sheetName || filenameBase);
  else writeCsv(spec, filename);

  return { filename, count: rows.length, format: chosen.id };
};

export default runExport;
